import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../../auth/firebaseConfig';
import { Tenancy } from '../../../types/tenancyTypes';
import { MaintenanceRequest } from './MaintenanceRequestCard';
import TenantMaintenanceView from './TenantMaintenanceView';
import { Timestamp } from 'firebase/firestore';

interface MaintenanceRequestFormData {
  listingId: string;
  issueDescription: string;
  photos: FileList;
  category: string;
}

const maintenanceCategories = [
  'Plumbing',
  'Electrical',
  'Appliances',
  'Heating/Cooling',
  'Pest Control',
  'General Repair',
  'Other'
];

const TenantMaintenanceContainer: React.FC = () => {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState<MaintenanceRequest[]>([]);
  const [tenancies, setTenancies] = useState<Tenancy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<(string | null)[]>([null]);

  const filteredRequestsForTenant = useMemo(() => {
    return requests;
  }, [requests]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<MaintenanceRequestFormData>();

  const photoFiles = watch('photos');

  const handleRemovePhoto = (indexToRemove: number) => {
    const currentFiles = Array.from(watch('photos') || []);
    const updatedFiles = currentFiles.filter(
      (_, index) => index !== indexToRemove
    );

    const dataTransfer = new DataTransfer();
    updatedFiles.forEach((file) => dataTransfer.items.add(file));
    setValue('photos', dataTransfer.files);
  };

  useEffect(() => {
    if (photoFiles && photoFiles.length > 0) {
      const urls = Array.from(photoFiles).map((file) =>
        URL.createObjectURL(file)
      );
      setPhotoPreviews(urls);

      return () => {
        urls.forEach((url) => URL.revokeObjectURL(url));
      };
    } else {
      setPhotoPreviews([]);
    }
  }, [photoFiles]);

  const fetchRequests = useCallback(
    async (options?: { pageIndex?: number; cursor?: string | null }) => {
      if (!currentUser) return;
      setIsLoading(true);
      try {
        const baseUrl = `${CLOUD_FUNCTIONS_BASE_URL}/getMaintenanceRequestsForTenant?tenantId=${currentUser.uid}`;
        const url = new URL(baseUrl);
        url.searchParams.set('pageSize', '10');

        const targetPage = options?.pageIndex ?? 1;
        const cursor = options?.cursor ?? null;

        if (cursor) url.searchParams.set('pageToken', cursor);

        const response = await fetch(url.toString());
        const data = await response.json();

        if (response.ok) {
          const parsedRequests = (data.requests || []).map((req: any) => ({
            ...req,
            createdAt: req.createdAt?._seconds
              ? new Timestamp(
                  req.createdAt._seconds,
                  req.createdAt._nanoseconds
                )
              : typeof req.createdAt === 'string'
                ? Timestamp.fromDate(new Date(req.createdAt))
                : req.createdAt,
            updatedAt: req.updatedAt?._seconds
              ? new Timestamp(
                  req.updatedAt._seconds,
                  req.updatedAt._nanoseconds
                )
              : typeof req.updatedAt === 'string'
                ? Timestamp.fromDate(new Date(req.updatedAt))
                : req.updatedAt
          }));
          setRequests(parsedRequests);
          setNextPageToken(data.nextPageToken || null);
          if (data.nextPageToken) {
            setPageCursors((prev) => {
              const newCursors = [...prev];
              if (!newCursors[targetPage]) {
                newCursors[targetPage] = data.nextPageToken;
              }
              return newCursors;
            });
          }
        } else {
          toast.error('Failed to fetch requests');
        }
      } catch (error) {
        toast.error('Error loading maintenance requests.');
      } finally {
        setIsLoading(false);
      }
    },
    [currentUser]
  );

  const fetchTenancies = useCallback(async () => {
    if (!currentUser) return;
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/getTenanciesForTenant?tenantId=${currentUser.uid}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setTenancies(data.tenancies || []);
      } else {
        console.error('Failed to fetch tenancies');
      }
    } catch (error) {
      toast.error('Error loading properties.');
    }
  }, [currentUser]);

  useEffect(() => {
    setNextPageToken(null);
    setPage(1);
    setPageCursors([null]);
    fetchRequests({ pageIndex: 1, cursor: null });
    fetchTenancies();
  }, [fetchRequests, fetchTenancies]);

  const goToPage = async (targetPage: number) => {
    setIsLoadingMore(true);
    const cursor = pageCursors[targetPage - 1];
    await fetchRequests({ pageIndex: targetPage, cursor });
    setPage(targetPage);
    setIsLoadingMore(false);
  };

  const onSubmit = async (data: MaintenanceRequestFormData) => {
    if (!currentUser) return;

    setIsSubmitting(true);
    const formData = new FormData();
    const selectedTenancy = tenancies.find(
      (t) => t.listingId === Number(data.listingId)
    );

    if (!selectedTenancy) {
      toast.error('Could not find tenancy for the selected property.');
      setIsSubmitting(false);
      return;
    }

    formData.append('landlordId', selectedTenancy.landlordId);
    formData.append('tenantId', currentUser.uid);
    formData.append('listingId', data.listingId);
    formData.append('issueDescription', data.issueDescription);
    formData.append('category', data.category);
    if (data.photos) {
      Array.from(data.photos).forEach((photo) =>
        formData.append('photos', photo)
      );
    }

    try {
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/createMaintenanceRequest`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (response.ok) {
        toast.success('Maintenance request submitted successfully!');
        reset();
        setShowForm(false);
        fetchRequests({ pageIndex: 1, cursor: null });
      } else {
        toast.error('Failed to submit request');
      }
    } catch (error) {
      toast.error('Error submitting request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  return (
    <TenantMaintenanceView
      tenancies={tenancies}
      isLoading={isLoading}
      requests={requests}
      filteredRequests={filteredRequestsForTenant}
      page={page}
      hasNext={Boolean(nextPageToken)}
      onPageChange={goToPage}
      isLoadingPage={isLoadingMore}
      showForm={showForm}
      setShowForm={setShowForm}
      isSubmitting={isSubmitting}
      photoPreviews={photoPreviews}
      handleRemovePhoto={handleRemovePhoto}
      onSubmit={onSubmit}
      register={register}
      errors={errors}
      handleSubmit={handleSubmit}
      maintenanceCategories={maintenanceCategories}
    />
  );
};

export default TenantMaintenanceContainer;
